/**
 * Checkout hold countdown helpers (matches MarketplaceConstants.CheckoutReservationMinutes).
 *
 * Prefer server `holdRemainingSeconds` for display so a PC clock ahead/behind Azure
 * does not shrink a fresh 15‑min hold to ~8m (or stretch it past 15m).
 */

export function parseReservedUntil(iso: string | null | undefined): number | null {
    if (!iso) return null;
    const ms = Date.parse(iso);
    return Number.isFinite(ms) ? ms : null;
}

/**
 * Client-local end time for ticking the countdown.
 * Built once from the API payload so wall-clock skew is absorbed at receive time.
 */
export type HoldCountdownAnchor = {
    endClientMs: number;
};

/**
 * Anchor countdown from server remaining seconds when present; else reservedUntil (skew-prone).
 */
export function createHoldCountdownAnchor(
    holdRemainingSeconds?: number | null,
    reservedUntil?: string | null,
    nowMs: number = Date.now()
): HoldCountdownAnchor | null {
    if (holdRemainingSeconds != null && Number.isFinite(holdRemainingSeconds)) {
        const sec = Math.max(0, Math.floor(holdRemainingSeconds));
        return { endClientMs: nowMs + sec * 1000 };
    }
    const until = parseReservedUntil(reservedUntil);
    if (until == null) return null;
    return { endClientMs: until };
}

/** Remaining ms from an anchor; 0 if expired or missing. */
export function remainingFromAnchor(
    anchor: HoldCountdownAnchor | null,
    nowMs: number = Date.now()
): number {
    if (!anchor) return 0;
    return Math.max(0, anchor.endClientMs - nowMs);
}

/** @deprecated Prefer createHoldCountdownAnchor + remainingFromAnchor (server remaining). */
export function remainingMs(reservedUntilMs: number | null, nowMs: number = Date.now()): number {
    if (reservedUntilMs == null) return 0;
    return Math.max(0, reservedUntilMs - nowMs);
}

/** Format as m:ss or mm:ss */
export function formatCountdown(ms: number): string {
    const totalSec = Math.ceil(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Whether the listing is still in a checkout hold for display purposes. */
export function isReservationActive(
    isReserved: boolean | undefined,
    reservedUntil: string | null | undefined,
    nowMs: number = Date.now(),
    holdRemainingSeconds?: number | null
): boolean {
    if (!isReserved) return false;
    if (holdRemainingSeconds != null && Number.isFinite(holdRemainingSeconds)) {
        return holdRemainingSeconds > 0;
    }
    const until = parseReservedUntil(reservedUntil);
    if (until == null) return !!isReserved;
    return remainingMs(until, nowMs) > 0;
}
